import { filter, map, Subject } from "rxjs";
import { s, Shape } from "../shapes";
import { Channel } from "./channel";
import { Http } from "./http";
import { WebSocket as WebSocketRaw } from "./websocket";
import { Endpoint } from "./endpoint2";

/**
 * A HttpEndpoint is a particular handler for
 * a path in a HttpApi. It should handle the variety of methods
 */
export type HttpEndpoint = 
  | HttpEndpoint.Json
  | HttpEndpoint.WebSocket
  | HttpEndpoint.Rest
  | HttpEndpoint.Raw
//  | HttpEndpoint.ByteStream

export namespace HttpEndpoint {
  export type Method =
    | 'GET'
    | 'PUT'
    | 'POST'
    | 'DELETE'
    | 'OPTIONS'
    | 'HEAD'
    | 'CONNECT'
    | 'PATCH'

  // TODO: fix these to actually be accurate and typesafe
  export type Headers = Record<string, string | void | string[]>

  export type Request<TQuery, TBody, THeaders = Headers> = {
    query: TQuery,
    body: TBody,
    headers: THeaders,
  }
  export type Response<TBody, THeaders = Headers> = {
    status: number,
    body: TBody,
    headers: THeaders,
  }

  export type Query<T extends (Shape | undefined)> =
    undefined extends T
    ? null
    : Shape.Matches<Record<string, null | string>, Extract<T, Shape>> extends true
    ? Shape.Type.Of<Extract<T, Shape>>
    : Shape extends T ? {}
    : null;

  export type Client<T extends HttpEndpoint> =
    T extends Json ? Json.Client<T>
    : T extends WebSocket ? WebSocket.Client<T>
    : T extends Raw ? Raw.Client
    : never;


  export type ServerHandler<T extends HttpEndpoint> =
    T extends Json ? Json.ServerHandler<T>
    : T extends WebSocket ? WebSocket.ServerHandler<T>
    : T extends Raw ? Raw.ServerHandler
    : never;

  export type CrossOriginPolicy = {
    allowHeaders: string[];
  }

  export type Json = Json.Def;
  export namespace Json {
    export type Request<T extends MethodDef> = HttpEndpoint.Request<Query<T["query"]>, T["request"]>;

    export type Response<T extends MethodDef> = HttpEndpoint.Response<T["response"]>;

    export type MethodDef = { request: Shape.Any, response: Shape.Any, query: Shape.Any }

    export type Def = {
      type: 'json',
      methods: {
        [key in Method]?: MethodDef
      },
      cors: CrossOriginPolicy
    }


    export namespace Client {
      export type Method<T extends MethodDef = MethodDef> = (
        query: Query<T["query"]>,
        body: Shape.Type.Of<T["request"]>,
        headers?: Headers,
        http?: Http.Client,
      ) => Promise<Shape.Type.Of<T["response"]>>
    }
    export type Client<T extends Def> = {
        [TMethod in keyof T["methods"]]: T["methods"][TMethod] extends MethodDef
          ? Client.Method<T["methods"][TMethod]>
          : never
      }

    export namespace Server {
      export type Method<T extends MethodDef = MethodDef> = (
        query: Query<T["query"]>,
        body: Shape.Type.Of<T["request"]>,
        
        raw: Http.Request,
      ) => Promise<Shape.Type.Of<T["response"]> | HttpEndpoint.Response<Shape.Type.Of<T["response"]>>>
    }
    export type ServerHandler<T extends Def = Def> = {
      [TMethod in keyof T["methods"]]: T["methods"][TMethod] extends MethodDef
        ? Server.Method<T["methods"][TMethod]>
        : never
    }
  }
  export type Rest = Rest.Def;
  export namespace Rest {
    export type Def = {
      type: 'rest',
    }
  }

  export type Raw = {
    type: 'raw',
  }
  export namespace Raw {
    export type Client = (
      query?: Record<string, string>,
      method?: string,
      body?: null | ReadableStream,
      headers?: Record<string, string>,
      http?: Http.Client,
    ) => Promise<Http.Response>;

    export type ServerHandler = (
      query: Record<string, string>,
      method: string,
      body: null | ReadableStream,
      headers: Record<string, string>
    ) => Promise<Http.Response>;
  }

  export type WebSocket = WebSocket.Def;
  export namespace WebSocket {
    export type Def = {
      type: 'websocket',
      query: Shape.Any,
      protocol: string,

      message: {
        client: Shape.Any,
        server: Shape.Any
      }
    }

    export type ChannelType<T extends Def> = Channel.Type<
      Shape.Type.Of<T["message"]["client"]>,
      Shape.Type.Of<T["message"]["server"]>
    >;


    export type Client<T extends Def> = {
      connect(query: Query<T["query"]>): ClientConnection<T>
    }
    export type ClientConnection<T extends Def> = {
      disconnect(reason?: number): void,
      
      port: Channel.Port.Client<ChannelType<T>>,
      ready: Promise<void>,
      disconnected: Promise<{ reason: number }>,
    }

    export type ServerHandler<T extends Def> = {
      onConnection(connection: ServerConnection<T>, raw: WebSocketRaw.ServerConnection): void,
    }
    export type ServerConnection<T extends Def> = {
      query: Query<T["query"]>,

      disconnect(reason?: number): void,
      port: Channel.Port.Server<ChannelType<T>>,

      ready: Promise<void>,
      disconnected: Promise<{ reason: number }>,
    }
  }
}

const createURL = (baseURL: URL, query?: Record<string, string>, mapProtocol?: Record<string, string>) => {
  const url = new URL(baseURL);
  if (query) {
    for (const [key, value] of Object.entries(query)) {
      if (value)
        url.searchParams.set(key, value);
    }
  }
    
  if (mapProtocol) {
    url.protocol = mapProtocol[url.protocol];
  }
    
  return url;
}

const DEFAULT_CORS_POLICY: HttpEndpoint.CrossOriginPolicy = {
  allowHeaders: [],
}

export const HttpEndpoint = {
  createURL,
  Json<T extends HttpEndpoint.Json["methods"]>(methods: T, cors: HttpEndpoint.CrossOriginPolicy = DEFAULT_CORS_POLICY) {
    return { type: 'json', methods, cors } as const;
  },
  WebSocket<const Query extends Shape, const Client extends Shape, const Server extends Shape>(
    query: Query,
    client: Client,
    server: Server,
    protocol: string = ''
  ) {
    return { type: 'websocket', query, message: { client, server }, protocol } as const;
  },
  Raw(): HttpEndpoint.Raw {
    return { type: 'raw' }
  },

  Clients: {
    Raw(baseURL: URL, client: Http.Client): HttpEndpoint.Raw.Client {
      return async (query, method = 'GET', body = null, headers = {}) => {
        const url = createURL(baseURL, query);

        const response = client.request(url, { method, body, headers });
        return response;
      }
    },
    WebSocket<T extends HttpEndpoint.WebSocket>(
      baseURL: URL,
      def: T,
      client: WebSocketRaw.Client
    ): HttpEndpoint.WebSocket.Client<T> {
      return {
        connect(query) {
          const url = createURL(baseURL, query as any, { http: 'ws', https: 'wss' });

          const connection = client.connect(url, [def.protocol]);

          return {
            ready: connection.ready.then(() => {}),
            disconnect: connection.disconnect,
            disconnected: connection.disconnected,
            port: {
              send(message) {
                connection.port.send(JSON.stringify(message))
              },
              receive: connection.port.receive.pipe(map(message => {
                if (typeof message === 'string') {
                  return JSON.parse(message)
                }
                return null;
              }), filter(message => !!message))
            },
          }
        }
      }
    },
    Json<T extends HttpEndpoint.Json>(
      baseURL: URL,
      def: T,
      client: Http.Client
    ): HttpEndpoint.Json.Client<T> {
      const createJsonMethod = (method: HttpEndpoint.Method, methodDef: HttpEndpoint.Json.MethodDef): HttpEndpoint.Json.Client.Method => {
        return async (query, body, headers, overrideClient) => {
          const finalClient = overrideClient || client;

          const url = createURL(baseURL, query as any);

          let requestStream = !!body && new Blob([JSON.stringify(body)]).stream() || null;

          const response = await finalClient.request(url, { method, body: requestStream, headers: headers as any });
          
          const responseBody = response.body && new Response(response.body).json();

          return responseBody
        }
      }

      return Object.fromEntries(Object.entries(def.methods).map(([method, methodDef]) => {
        return [method, createJsonMethod(method as HttpEndpoint.Method, methodDef)]
      })) as HttpEndpoint.Json.Client<HttpEndpoint.Json> as HttpEndpoint.Json.Client<T>;
    }
  },

  Routes: {
    Raw(path: string, def: HttpEndpoint.Raw, handler: HttpEndpoint.Raw.ServerHandler): Http.Route {
      return {
        path,
        method: null,
        handler(request) {
          return handler(request.query, request.method, request.body, request.headers as any)
        },
      }
    },
    WebSocket<T extends HttpEndpoint.WebSocket>(
      path: string,
      def: T,
      handler: HttpEndpoint.WebSocket.ServerHandler<T>
    ): WebSocketRaw.Route {
      return {
        path,
        protocol: def.protocol,
        handler(raw) {
          const { query, path, port, ready, disconnect, disconnected, } = raw;
          handler.onConnection({
            query: query as HttpEndpoint.Query<T["query"]>,
            port: {
              send(message) {
                port.send(JSON.stringify(message))
              },
              receive: port.receive.pipe(map(message => {
                if (typeof message === 'string')
                  return JSON.parse(message);
                return null;
              })),
            },
            ready: ready,
            disconnect: disconnect,
            disconnected: disconnected
          }, raw);
        }
      }
    },
    Json<T extends HttpEndpoint.Json>(
      path: string,
      def: T,
      handlers: HttpEndpoint.Json.ServerHandler<T>,
    ): Http.Route[] {
      const createMethodRoute = (
        method: string,
        methodDef: HttpEndpoint.Json.MethodDef, 
        handler: HttpEndpoint.Json.Server.Method
      ): Http.Route => {
        return {
          path,
          method,
          async handler(request) {
            const bodyR = request.body && await new Response(request.body).json();

            const response = await handler(request.query, bodyR, request);
            if (typeof response === 'object' && response && 'status' in response) {
              const responseBody = new Blob([JSON.stringify(response.body)]).stream();

              return {
                status: response.status as number,
                body: responseBody,
                headers: response.headers as HttpEndpoint.Headers,
              }
            }
            const responseBody = new Blob([JSON.stringify(response)]).stream();

            return {
              status: 200,
              body: responseBody,
              headers: {},
            }
          }
        }
      }
      if (!handlers)
        throw new Error(`Missing implementation for "${path}"`);

      return Object.entries(handlers).map(([method, handler]) => {
        // force the types to be accurate
        const methodT = method as keyof T["methods"];
        const methods = def.methods as T["methods"];
        return createMethodRoute(method, methods[methodT] as HttpEndpoint.Json.MethodDef, handler)
      })
    }
  }
};
